/**
 * App shell + route guard (T017). Enforces Workspace SSO + domain restriction on the client side
 * (server callables + Security Rules are authoritative), and renders a role-aware layout.
 */
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { logout, signIn } from './services/auth';
import { useSession } from './hooks/useSession';
import { Onboard } from './pages/Onboard';
import { BillingIdentities } from './pages/BillingIdentities';
import { EditProject } from './pages/EditProject';
import { EditClient } from './pages/EditClient';
import { Roles } from './pages/Roles';
import { Roster } from './pages/Roster';

export default function App() {
  const { user, loading } = useSession();

  if (loading) return <main style={page}>Loading…</main>;

  if (!user) {
    return (
      <main style={page}>
        <h1>Cosmic Chimps · Control Plane</h1>
        <p>Sign in with your @cosmic-chimps.com Workspace account.</p>
        <button style={button} onClick={() => void signIn()}>
          Sign in with Google Workspace
        </button>
      </main>
    );
  }

  if (!user.isDomainUser) {
    return (
      <main style={page}>
        <h1>Access denied</h1>
        <p>{user.email} is not a @cosmic-chimps.com account.</p>
        <button style={button} onClick={() => void logout()}>
          Sign out
        </button>
      </main>
    );
  }

  return (
    <div>
      <header style={header}>
        <strong>Control Plane</strong>
        <nav style={{ display: 'flex', gap: 12 }}>
          <Link to="/onboard">Onboard</Link>
          <Link to="/billing">Billing identities</Link>
          <Link to="/edit">Edit project</Link>
          <Link to="/edit-client">Edit client</Link>
          {user.role === 'admin' && <Link to="/roles">Roles</Link>}
          {user.role === 'admin' && <Link to="/roster">Roster</Link>}
        </nav>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <small>
            {user.email} · <em>{user.role ?? 'read-only'}</em>
          </small>
          <button style={buttonSm} onClick={() => void logout()}>
            Sign out
          </button>
        </span>
      </header>
      <main style={page}>
        <Routes>
          <Route path="/onboard" element={<Onboard user={user} />} />
          <Route path="/billing" element={<BillingIdentities user={user} />} />
          <Route path="/edit" element={<EditProject user={user} />} />
          <Route path="/edit-client" element={<EditClient user={user} />} />
          <Route path="/roles" element={<Roles user={user} />} />
          <Route path="/roster" element={<Roster user={user} />} />
          <Route path="*" element={<Navigate to="/onboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const page: React.CSSProperties = { maxWidth: 880, margin: '0 auto', padding: 24, fontFamily: 'system-ui, sans-serif' };
const header: React.CSSProperties = { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 24px', borderBottom: '1px solid #ddd' };
const button: React.CSSProperties = { padding: '8px 16px', cursor: 'pointer' };
const buttonSm: React.CSSProperties = { padding: '4px 10px', cursor: 'pointer' };
